import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { catchError, finalize, tap } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { OrganizationTreeStore } from './organizations-tree.store';
import { getErrorMessage } from '../../../modules/shared/get-error-mesage';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Organization, OrganizationSchema } from '../../../modules/analytics/models/organization';
import { SignalStoreCache } from '../../../modules/shared/signal-store';
import { decodeTolerantResult } from '../../../modules/shared/schema-validation';

const STORE_NAME = 'organization-tree';

@Injectable({ providedIn: 'root' })
export class OrganizationTreeService {
  private orgStore = inject(OrganizationTreeStore);
  private cache = inject(SignalStoreCache);

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar
  ) {}

  getAll() {
    if (this.orgStore.loaded()) return EMPTY;
    // Always fetched from the API on page load (no IndexedDB cache), so admin changes show after a reload.
    this.orgStore.setLoading(true);
    return this.http
      .get<Organization[]>(`${environment.directoryApi}/user/shallow_organizations_tree`)
      .pipe(
        tap(x => {
          const { data } = decodeTolerantResult(OrganizationSchema.array(), x, 'organization-tree.getAll');
          this.orgStore.setAll(data);
        }),
        catchError(err => of(this.snackBar.open(getErrorMessage(err), 'OK', { panelClass: 'red-snackbar' }))),
        finalize(() => this.orgStore.setLoading(false))
      );
  }

  async refreshCache(): Promise<void> {
    // Invalidate IndexedDB first — once `loaded` drops, a concurrent
    // getAll() may re-fetch and must not hit the stale cache entry
    await this.cache.invalidate(STORE_NAME);
    this.orgStore.resetCache();
  }
}
